import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

const csv = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

export default function MediaKitEditor() {
  const { user, setUser } = useAuth();
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/mediakit/me").then((r) => setKit(r.data)).catch(() => setKit({}));
  }, []);

  if (!kit) return <div className="max-w-3xl mx-auto p-8 text-neutral-500">Loading…</div>;

  const update = (k, v) => setKit({ ...kit, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...kit,
        niches: typeof kit.niches === "string" ? kit.niches.split(",").map((s) => s.trim()).filter(Boolean) : kit.niches,
        past_brands:
          typeof kit.past_brands === "string"
            ? kit.past_brands.split(",").map((s) => s.trim()).filter(Boolean)
            : kit.past_brands,
        top_countries:
          typeof kit.top_countries === "string"
            ? kit.top_countries.split(",").map((s) => s.trim()).filter(Boolean)
            : kit.top_countries,
        followers_instagram: kit.followers_instagram ? Number(kit.followers_instagram) : undefined,
        followers_tiktok: kit.followers_tiktok ? Number(kit.followers_tiktok) : undefined,
        followers_youtube: kit.followers_youtube ? Number(kit.followers_youtube) : undefined,
        avg_views: kit.avg_views ? Number(kit.avg_views) : undefined,
        engagement_rate: kit.engagement_rate ? Number(kit.engagement_rate) : undefined,
        audience_female_pct: kit.audience_female_pct ? Number(kit.audience_female_pct) : undefined,
        starting_rate: kit.starting_rate ? Number(kit.starting_rate) : undefined,
      };
      const r = await api.put("/mediakit/me", payload);
      setKit(r.data);
      if (r.data.handle && user) setUser({ ...user, handle: r.data.handle });
      toast.success("Media kit saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">// public link</p>
          <h1 className="font-display font-black text-5xl tracking-tighter">Your Media Kit</h1>
          <p className="mt-2 text-sm font-mono text-neutral-500">
            dap.app/{kit.handle || user?.handle}
          </p>
        </div>
        <a href={`/${kit.handle || user?.handle}`} target="_blank" rel="noreferrer" data-testid="mediakit-view-public">
          <Button variant="ghost" className="rounded-full border border-white/15">
            View public <ExternalLink className="w-4 h-4 ml-1" />
          </Button>
        </a>
      </div>

      <form onSubmit={submit} className="space-y-8" data-testid="mediakit-form">
        <Section title="Identity">
          <Field label="Display name">
            <Input value={kit.display_name || ""} onChange={(e) => update("display_name", e.target.value)}
              data-testid="mk-name-input" className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Handle">
            <Input value={kit.handle || ""} onChange={(e) => update("handle", e.target.value.toLowerCase())}
              data-testid="mk-handle-input" className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Tagline">
            <Input value={kit.tagline || ""} onChange={(e) => update("tagline", e.target.value)}
              placeholder="e.g. Beauty creator open to fragrance & skincare partnerships"
              data-testid="mk-tagline-input" className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Bio">
            <Textarea rows={3} value={kit.bio || ""} onChange={(e) => update("bio", e.target.value)}
              data-testid="mk-bio-textarea" className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Niches (comma separated)">
            <Input value={csv(kit.niches)} onChange={(e) => update("niches", e.target.value)}
              placeholder="Beauty, Wellness, Travel" data-testid="mk-niches-input"
              className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Location">
            <Input value={kit.location || ""} onChange={(e) => update("location", e.target.value)}
              className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Public contact email">
            <Input type="email" value={kit.email_public || ""} onChange={(e) => update("email_public", e.target.value)}
              data-testid="mk-email-input" className="bg-[#0e0e0e] border-white/10" />
          </Field>
        </Section>

        <Section title="Socials & Stats">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Instagram handle">
              <Input value={kit.instagram_handle || ""} onChange={(e) => update("instagram_handle", e.target.value)}
                className="bg-[#0e0e0e] border-white/10" />
            </Field>
            <Field label="Instagram followers">
              <Input type="number" value={kit.followers_instagram || ""} onChange={(e) => update("followers_instagram", e.target.value)}
                data-testid="mk-ig-followers-input" className="bg-[#0e0e0e] border-white/10 font-mono" />
            </Field>
            <Field label="TikTok handle">
              <Input value={kit.tiktok_handle || ""} onChange={(e) => update("tiktok_handle", e.target.value)}
                className="bg-[#0e0e0e] border-white/10" />
            </Field>
            <Field label="TikTok followers">
              <Input type="number" value={kit.followers_tiktok || ""} onChange={(e) => update("followers_tiktok", e.target.value)}
                className="bg-[#0e0e0e] border-white/10 font-mono" />
            </Field>
            <Field label="YouTube handle">
              <Input value={kit.youtube_handle || ""} onChange={(e) => update("youtube_handle", e.target.value)}
                className="bg-[#0e0e0e] border-white/10" />
            </Field>
            <Field label="YouTube subs">
              <Input type="number" value={kit.followers_youtube || ""} onChange={(e) => update("followers_youtube", e.target.value)}
                className="bg-[#0e0e0e] border-white/10 font-mono" />
            </Field>
            <Field label="Avg views / post">
              <Input type="number" value={kit.avg_views || ""} onChange={(e) => update("avg_views", e.target.value)}
                className="bg-[#0e0e0e] border-white/10 font-mono" />
            </Field>
            <Field label="Engagement rate (%)">
              <Input type="number" step="0.1" value={kit.engagement_rate || ""} onChange={(e) => update("engagement_rate", e.target.value)}
                className="bg-[#0e0e0e] border-white/10 font-mono" />
            </Field>
          </div>
        </Section>

        <Section title="Audience">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Female audience %">
              <Input type="number" value={kit.audience_female_pct || ""} onChange={(e) => update("audience_female_pct", e.target.value)}
                className="bg-[#0e0e0e] border-white/10 font-mono" />
            </Field>
            <Field label="Age range">
              <Input value={kit.audience_age_range || ""} onChange={(e) => update("audience_age_range", e.target.value)}
                placeholder="18-34" className="bg-[#0e0e0e] border-white/10" />
            </Field>
          </div>
          <Field label="Top countries (comma separated)">
            <Input value={csv(kit.top_countries)} onChange={(e) => update("top_countries", e.target.value)}
              placeholder="United States, Canada, UK" className="bg-[#0e0e0e] border-white/10" />
          </Field>
        </Section>

        <Section title="Deal Info">
          <Field label="Past brand partners (comma separated)">
            <Input value={csv(kit.past_brands)} onChange={(e) => update("past_brands", e.target.value)}
              placeholder="Nike, Sephora, Chipotle" data-testid="mk-past-brands-input"
              className="bg-[#0e0e0e] border-white/10" />
          </Field>
          <Field label="Starting rate ($ USD)">
            <Input type="number" value={kit.starting_rate || ""} onChange={(e) => update("starting_rate", e.target.value)}
              className="bg-[#0e0e0e] border-white/10 font-mono" />
          </Field>
        </Section>

        <Button
          type="submit"
          disabled={busy}
          className="w-full h-12 rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
          data-testid="mediakit-save-btn"
        >
          {busy ? "Saving…" : "Save media kit"}
        </Button>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="p-6 rounded-2xl bg-[#141414] border border-white/5">
    <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-4">// {title}</p>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-mono text-neutral-500 mb-1 block">{label}</span>
    {children}
  </label>
);
